/**
 * RequestDeliverablesEditor — the one control that presents request deliverables, rendered in two places:
 *
 *  - `RequestDeliverablesStep` (the Deliverables section) drives it against the live service.
 *  - `RequestDeliverablesDialog` (submit) drives it against the same live service.
 *
 * It is purely presentational: it never reads or writes storage, it only reports
 * `onAdd` / `onEdit` / `onRemove`. That is what lets both controllers share it (addendum,
 * "Build note"). Both controllers hold those reports as pending changes and let the
 * request's own Save / Submit commit them, so there is no staged mode and no `flushPending`
 * handle here.
 *
 * **One checklist, and the tick IS the change.** Until 2026-08-12 this was two zones — a
 * "Requested deliverables" list with per-row Edit/Remove, and an "Add a deliverable" block
 * whose ticks only counted once "Add all checked" was pressed. That second commit is what
 * users kept missing: a ticked box with a typed comment was invisible to every caller, so
 * the submit dialog rejected a form the user could see was filled in. Now ticking calls
 * `onAdd`, unticking calls `onRemove`, and the comment reports through `onEdit` as it is
 * typed — one commit, performed by the same Save that commits every other field.
 *
 * Deliberately does **not** use `src/components/forms/FormCheckbox.tsx` or `FormInput`:
 * both call `useFormPermissions`, which resolves against the stage permission matrices and
 * would render the selection read-only inside a modal on a `CONFIGURATION` request. The
 * self-contained row below follows `ImageSelectionModal`'s `SelectRow` precedent instead.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';

import {
  DELIVERABLE_COMMENT_INSERT_MAX,
  getDeliverableCommentInfo,
} from '../../constants/deliverableCommentExamples';
import { useResponsive } from '../../hooks/useResponsive';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, fontWeight, lineHeight } from '../../theme/typography';
import {
  REQUEST_DELIVERABLE_TYPES,
  getRequestDeliverableLabel,
} from '../../types/requestDeliverables';
import {
  appendExampleText,
  exampleInsertAvailability,
  fieldInfoPanelA11y,
  scheduleDeferred,
} from '../../utils/fieldExampleInsert';
import { FieldTextAssist } from '../forms/FieldTextAssist';
import {
  FORM_FIELD_TRAILING_ICON_SIZE,
  FormFieldTrailingIcon,
} from '../forms/FormFieldTrailingIcon';
import { Check, TextAssistPlaceholderIcon } from '../icons';

import type {
  RequestDeliverable,
  RequestDeliverableEntry,
  RequestDeliverableType,
} from '../../types/requestDeliverables';

const isCatalogueType = (type: RequestDeliverableType): boolean =>
  REQUEST_DELIVERABLE_TYPES.includes(type);

const commentValue = (
  drafts: Map<RequestDeliverableType, string>,
  requestedByType: Map<RequestDeliverableType, RequestDeliverable>,
  type: RequestDeliverableType,
): string => drafts.get(type) ?? requestedByType.get(type)?.comment ?? '';

export interface RequestDeliverablesEditorProps {
  /** The request's deliverables, pending changes included. Owned by the caller. */
  deliverables: RequestDeliverable[];
  /**
   * Reports a newly ticked type. Takes an array because both controllers apply entries in
   * bulk (`applyDeliverableEntries`) and neither should grow a second entry point; the tick
   * itself only ever sends one.
   */
  onAdd: (entries: RequestDeliverableEntry[]) => void;
  /**
   * Reports a comment as it is typed. The **type is not editable** — it is the lookup key,
   * not a value (FR15).
   */
  onEdit: (itemType: RequestDeliverableType, comment: string) => void;
  /** Reports an unticked type. Removal is a delete, not an archive (FR16). */
  onRemove: (itemType: RequestDeliverableType) => void;
  /**
   * Read-only: renders what was asked for and nothing else. No checkboxes, no comment
   * toggles, no inputs — a stored comment renders as text.
   *
   * Three things reach it, all through `RequestDeliverablesStep`: a Closed request (FR18),
   * a saved request nobody has pressed Edit on, and a viewer whose role cannot write this
   * field at this stage. The submit dialog never passes it.
   */
  disabled?: boolean;
  /**
   * While read-only, also name the types that were NOT requested, so a reader can see what
   * else could be asked for. Ignored when editable, where the checklist already shows them.
   *
   * A boolean rather than the `readOnlyReason` itself, so this component stays ignorant of
   * WHY it is read-only. `RequestDeliverablesStep` owns that mapping — it suppresses the
   * group on a Closed request, where nothing can be requested and a list of what was not
   * asked for reads as a reproach rather than an option.
   */
  showAvailableOptions?: boolean;
}

/** Unique stored types the canonical list does not name — retired codes such as `TECHNICAL_DRAWING`. */
const unlistedStoredTypes = (
  deliverables: RequestDeliverable[],
): RequestDeliverableType[] => {
  const seen = new Set<RequestDeliverableType>();
  return deliverables
    .map(deliverable => deliverable.itemType)
    .filter(type => {
      if (REQUEST_DELIVERABLE_TYPES.includes(type) || seen.has(type)) {
        return false;
      }
      seen.add(type);
      return true;
    });
};

export const RequestDeliverablesEditor: React.FC<
  RequestDeliverablesEditorProps
> = ({
  deliverables,
  onAdd,
  onEdit,
  onRemove,
  disabled = false,
  showAvailableOptions = false,
}) => {
  /**
   * The row folds on compact widths. It has now been argued both ways, so both are recorded
   * here — removing this branch a third time needs a better reason than the last one.
   *
   * **2026-08-12, fold removed.** Earlier versions dropped the controls below the label on
   * native and narrow web. That existed because the row carried Edit **and** Remove — roughly
   * 130px of fixed-width buttons — which squeezed the text to about ten characters on a phone.
   * A single short toggle was judged not to need it, and keeping the fold would separate the
   * toggle from the label it acts on at exactly the width where that pairing matters most.
   *
   * **2026-08-17, fold reinstated (GitLab #1450, QA iOS DQ v1.13.2).** That judgement was sound
   * about the buttons and too generous about the link. Measured, one line needs ~340pt of card
   * body:
   *
   *     20  checkbox (formCheckboxOuterSize)
   *     10  gap (formCheckboxLabelGap)
   *    204  longest label "3D Layout - Rendering images" @ fontSize.formLabel 14
   *      8  gap (formFieldStackedActionGap)
   *     98  "Show comment" @ fontSize.cardSubtitle 13 + 2x8 padding
   *
   * The QA phone has ~258pt once the collapsed rail, page padding and `cardPadding` come off,
   * so the row was ~80pt short and "Add comment" rendered on top of the label. A deficit that
   * size cannot be shrunk away; something has to move to a second line. What the 2026-08-12
   * reasoning does still win is the pairing argument, which is why the stacked toggle indents
   * to the label's text start rather than to the card edge.
   *
   * `isMobile` (viewport < 768) is the gate, NOT a measured width, and it is deliberately
   * generous. The geometry, corrected in review — an earlier draft of this note derived the
   * gate from the 252pt expanded rail, which is a configuration that cannot occur here:
   *
   *   - At >= `REQUEST_FORM_MOBILE_LAYOUT_MAX_WIDTH` (1200) the shell embeds the sidebar and
   *     the rail can be 252pt, but the body is then ~876pt and nothing is tight.
   *   - Below 1200 the shell switches to the rail-plus-overlay layout, so the persistent rail
   *     is the 60pt collapsed one and the expanded drawer floats over the content rather than
   *     displacing it. Body = W - 60 - 40 (page) - 32 (card) = W - 132, which crosses the
   *     340pt requirement at about **W = 472**.
   *
   * So 768 carries ~300pt of headroom over the arithmetic threshold. That is intentional, not
   * a derivation: the 204pt label figure is an estimate from average glyph width, and
   * `allowFontScaling` defaults on, so Dynamic Type inflates the requirement well past 340
   * without the gate noticing. Folding early costs the label/toggle pairing on iPad mini
   * portrait and narrow browser windows; overlapping text costs more. Re-measure before
   * retuning this — `responsive-ui.md` is explicit that this arithmetic is not evidence.
   *
   * Note the separate `selectLabel` `flexShrink` below — that fixes the overlap itself, and is
   * needed at every width, fold or no fold.
   */
  const { isMobile } = useResponsive();

  /**
   * The open comment boxes and what each one displays — **one piece of state, not two**.
   * A type is open if and only if it has an entry here.
   *
   * The value is deliberately NOT `deliverable.comment`: `applyDeliverableComment` runs
   * `normalizeComment`, which trims, so an input bound to the stored value loses a space at
   * the moment the user types it. The draft holds the raw text, `onEdit` reports it on every
   * keystroke, and the store keeps its trimmed copy.
   *
   * Open-ness lived in a second `openTypes` array until review on 2026-08-12. Two states for
   * one fact meant the toggle had to read one of them from the render closure to decide which
   * way to go, so two presses landing in the same batch both took the opening branch: the
   * second press was swallowed and re-seeded the draft from the store, discarding what the box
   * was showing. Deriving from `prev` inside a single updater cannot go wrong that way.
   *
   * A Map rather than a keyed object: indexing an object by a variable trips
   * `security/detect-object-injection`, and suppressing that rule is worse than using the
   * collection that does not raise it.
   */
  const [drafts, setDrafts] = useState<Map<RequestDeliverableType, string>>(
    () => new Map(),
  );
  const [assistType, setAssistType] = useState<RequestDeliverableType | null>(
    null,
  );
  const [pendingOpenType, setPendingOpenType] =
    useState<RequestDeliverableType | null>(null);
  const commentInputRefs = useRef(
    new Map<RequestDeliverableType, TextInput | null>(),
  );
  const focusCommentTypeRef = useRef<RequestDeliverableType | null>(null);
  const unmountedRef = useRef(false);
  const [commentCaret, setCommentCaret] = useState<{
    type: RequestDeliverableType;
    start: number;
    end: number;
  } | null>(null);
  /**
   * Stored types the canonical list no longer names, remembered for the lifetime of the
   * mount rather than derived from the current list.
   *
   * Derived, the row vanished the moment it was unticked — `unlistedStoredTypes` went empty,
   * so the checklist had no row left to tick again and the deliverable could not be put back
   * without cancelling the whole edit. Canonical types never had that problem: their row
   * stays, unticked. Remembering the type keeps a retired code behaving like every other row.
   */
  const [seenUnlistedTypes, setSeenUnlistedTypes] = useState<
    RequestDeliverableType[]
  >(() => unlistedStoredTypes(deliverables));

  const requestedByType = new Map(
    deliverables.map(deliverable => [deliverable.itemType, deliverable]),
  );

  const rowTypes = [...REQUEST_DELIVERABLE_TYPES, ...seenUnlistedTypes];
  // Read-only shows the ask, not the menu it was chosen from.
  const visibleTypes = disabled
    ? rowTypes.filter(type => requestedByType.has(type))
    : rowTypes;

  /**
   * The catalogue minus what was asked for, named but not offered.
   *
   * Read-only answered "what did we ask for?" and silently refused "what could we ask for?",
   * so a requestor who had never seen the list had to press Edit purely to browse it.
   *
   * Built from `REQUEST_DELIVERABLE_TYPES` rather than from `rowTypes`, which deliberately
   * excludes the retired codes remembered in `seenUnlistedTypes`: a type that no longer
   * exists cannot be requested, so naming it as available would be a lie.
   */
  const availableTypes =
    disabled && showAvailableOptions
      ? REQUEST_DELIVERABLE_TYPES.filter(type => !requestedByType.has(type))
      : [];

  useEffect(() => {
    const unlisted = unlistedStoredTypes(deliverables);
    setSeenUnlistedTypes(prev => {
      const fresh = unlisted.filter(type => !prev.includes(type));
      return fresh.length === 0 ? prev : [...prev, ...fresh];
    });
  }, [deliverables]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  /**
   * Drops the draft of any type that is no longer requested, and every draft once the section
   * goes read-only.
   *
   * Unticking and Hide already close their own box; this is for the cases where a type leaves
   * the list without passing through either — the caller reloading, a Cancel rolling the
   * pending list back, a hydrated draft replacing it. Without this the entry outlived the box:
   * `isOpen` went false because the row was gone, and if the type came back the box sprang
   * open by itself holding text the user had abandoned, while the store held something else.
   * That is the same bug the old edit panel had, one state removed.
   *
   * Nothing is lost by dropping a draft — every keystroke was already reported through
   * `onEdit`, so it is a display copy and not the only record of the text. Returning `prev`
   * untouched when there is nothing to drop is what keeps this off the render loop, since
   * `deliverables` is a new array on every keystroke.
   */
  useEffect(() => {
    setDrafts(prev => {
      if (prev.size === 0) return prev;
      if (disabled) return new Map();
      const live = new Set(
        deliverables.map(deliverable => deliverable.itemType),
      );
      const stale = [...prev.keys()].filter(type => !live.has(type));
      if (stale.length === 0) return prev;
      const next = new Map(prev);
      stale.forEach(type => next.delete(type));
      return next;
    });
  }, [deliverables, disabled]);

  useEffect(() => {
    if (disabled) {
      setAssistType(null);
      setPendingOpenType(null);
    }
  }, [disabled]);

  useEffect(() => {
    if (pendingOpenType == null) {
      return;
    }
    const match = deliverables.find(
      deliverable => deliverable.itemType === pendingOpenType,
    );
    if (!match) {
      return;
    }
    const type = pendingOpenType;
    const stored = match.comment ?? '';
    setDrafts(prev => {
      if (prev.has(type)) {
        return prev;
      }
      return new Map(prev).set(type, stored);
    });
    focusCommentTypeRef.current = type;
    setCommentCaret({
      type,
      start: stored.length,
      end: stored.length,
    });
    setPendingOpenType(null);
  }, [deliverables, pendingOpenType]);

  useEffect(() => {
    const type = focusCommentTypeRef.current;
    if (type == null || !drafts.has(type)) {
      return;
    }
    focusCommentTypeRef.current = null;
    scheduleDeferred(() => {
      if (unmountedRef.current) {
        return;
      }
      commentInputRefs.current.get(type)?.focus();
    });
  }, [drafts]);

  const closeComment = (type: RequestDeliverableType) => {
    setDrafts(prev => {
      if (!prev.has(type)) return prev;
      const next = new Map(prev);
      next.delete(type);
      return next;
    });
  };

  const handleToggleType = (type: RequestDeliverableType) => {
    if (disabled) return;
    if (requestedByType.has(type)) {
      if (pendingOpenType === type) {
        setPendingOpenType(null);
      }
      closeComment(type);
      onRemove(type);
      return;
    }
    // Insert already reported this type with Helper Text. A tick before the parent
    // echoes it would send onAdd('') and, on Create Request, last-write-wins empty.
    if (pendingOpenType === type) {
      return;
    }
    onAdd([{ itemType: type, comment: '' }]);
  };

  const handleToggleComment = (type: RequestDeliverableType) => {
    if (disabled) return;
    // Both directions decided from `prev`, so a second press in the same batch closes the box
    // the first one opened instead of re-opening it. The seed reads the prop, not state.
    setDrafts(prev => {
      const next = new Map(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.set(type, requestedByType.get(type)?.comment ?? '');
      }
      return next;
    });
  };

  const handleCommentChange = (type: RequestDeliverableType, value: string) => {
    if (disabled) return;
    if (commentCaret?.type === type) {
      setCommentCaret(null);
    }
    setDrafts(prev => new Map(prev).set(type, value));
    onEdit(type, value);
  };

  const insertHelperText = (
    type: RequestDeliverableType,
    helperText: string,
  ): boolean => {
    if (disabled) {
      return false;
    }
    const current = commentValue(drafts, requestedByType, type);
    const { value, inserted } = appendExampleText(
      current,
      helperText,
      DELIVERABLE_COMMENT_INSERT_MAX,
    );
    if (!inserted) {
      return false;
    }
    if (!requestedByType.has(type)) {
      onAdd([{ itemType: type, comment: value }]);
      setPendingOpenType(type);
      return true;
    }
    setDrafts(prev => new Map(prev).set(type, value));
    onEdit(type, value);
    focusCommentTypeRef.current = type;
    setCommentCaret({ type, start: value.length, end: value.length });
    return true;
  };

  const assistPanel =
    assistType != null && isCatalogueType(assistType)
      ? getDeliverableCommentInfo(assistType)
      : null;
  const assistComment =
    assistType != null ? commentValue(drafts, requestedByType, assistType) : '';

  return (
    <View testID="request-deliverables-editor">
      {/*
        One card across both modes (human decision, 2026-08-12). Read-only used to be a bare
        list on the page background — the only block on Request Detail that was not in a card,
        which read as unfinished beside the Summary sections and device tables. Wrapping only
        the read-only half would have been worse: pressing Edit would dissolve the card, and a
        container that vanishes reads as a rendering fault rather than a mode change. So the
        card holds still and its contents swap.
      */}
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <Text style={styles.cardTitle}>Requested deliverables</Text>
          {/*
            Neutral pill, following `FieldFillCountBadge`, not the Summary step's orange one:
            this is a subordinate section on a read-only page, and the accent colour would
            shout louder than the content deserves. Counts what is REQUESTED, which is why it
            still reads correctly in edit mode where the body also lists unticked types.
          */}
          <View style={styles.countBadge} testID="request-deliverables-count">
            <Text style={styles.countText}>{deliverables.length}</Text>
          </View>
        </View>

        <View style={styles.cardBody}>
          {deliverables.length === 0 && (
            <Text style={styles.emptyText} testID="request-deliverables-empty">
              No deliverables requested yet.
            </Text>
          )}

          {visibleTypes.map((type, index) => {
            const deliverable = requestedByType.get(type);
            const isRequested = !!deliverable;
            // `!disabled` as well as the state check, so an open box is gone on the very render
            // that turns read-only rather than one commit later when the pruning effect runs.
            // `isRequested` too, so a box cannot outlive the row it belongs to.
            const isOpen = !disabled && isRequested && drafts.has(type);
            const label = getRequestDeliverableLabel(type);
            const storedComment = deliverable?.comment ?? '';
            /**
             * "Show", not "Edit", when one is stored: pressing it reveals a comment the row is
             * not currently displaying, and that is what the word has to promise. Editing is
             * what you can then do, not what the press does.
             */
            const toggleLabel = isOpen
              ? 'Hide comment'
              : storedComment
              ? 'Show comment'
              : 'Add comment';

            return (
              <View
                key={type}
                // Hairline between rows, none after the last. Without it a comment and the label
                // of the NEXT row read as one block, so nobody could tell which item a comment
                // belonged to — a comprehension problem wearing a styling costume.
                style={[
                  styles.row,
                  index < visibleTypes.length - 1 && styles.rowDivided,
                ]}
                testID={
                  isRequested ? `request-deliverable-item-${type}` : undefined
                }
              >
                <View
                  style={[
                    styles.rowHeader,
                    isMobile && styles.rowHeaderStacked,
                  ]}
                  testID={`request-deliverable-header-${type}`}
                >
                  {disabled ? (
                    <Text style={styles.requestedLabel}>{label}</Text>
                  ) : (
                    <View
                      style={styles.labelWithAssist}
                      testID={`request-deliverable-label-line-${type}`}
                    >
                      <TouchableOpacity
                        style={styles.selectRow}
                        onPress={() => handleToggleType(type)}
                        testID={`request-deliverable-select-${type}`}
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked: isRequested }}
                        accessibilityLabel={label}
                      >
                        <View
                          style={[
                            styles.checkbox,
                            isRequested && styles.checkboxOn,
                          ]}
                        >
                          {isRequested && (
                            <Check
                              size={13}
                              color={colors.textLight}
                              strokeWidth={2.5}
                            />
                          )}
                        </View>
                        <Text style={styles.selectLabel}>{label}</Text>
                      </TouchableOpacity>
                      {isCatalogueType(type) ? (
                        <FormFieldTrailingIcon
                          onPress={() => setAssistType(type)}
                          accessibilityLabel={`Insert helper text into ${label} comment`}
                          accessibilityHint={fieldInfoPanelA11y(
                            getDeliverableCommentInfo(type),
                          )}
                          testID={`request-deliverable-text-assist-${type}`}
                          style={styles.textAssistTrigger}
                        >
                          <TextAssistPlaceholderIcon
                            size={FORM_FIELD_TRAILING_ICON_SIZE}
                            color={colors.textSecondary}
                          />
                        </FormFieldTrailingIcon>
                      ) : null}
                    </View>
                  )}

                  {!disabled && isRequested && (
                    <View
                      style={[
                        styles.headerTrailing,
                        isMobile && styles.headerTrailingStacked,
                      ]}
                    >
                      <TouchableOpacity
                        onPress={() => handleToggleComment(type)}
                        style={[
                          styles.rowAction,
                          isMobile && styles.rowActionStacked,
                        ]}
                        testID={`request-deliverable-comment-toggle-${type}`}
                        accessibilityRole="button"
                        accessibilityLabel={`${toggleLabel} for ${label}`}
                      >
                        <Text style={styles.rowActionText}>{toggleLabel}</Text>
                      </TouchableOpacity>
                    </View>
                  )}
                </View>

                {/*
              Read-only only. While editable a stored comment stays behind "Show comment",
              so eight rows read as a list rather than a wall of prose.
              Read-only renders no toggle at all, so the same rule there would make the
              comment unreachable rather than merely collapsed — a Closed request has to
              still show what was asked for (FR18).

              A row with no comment says so rather than dropping the line. Omitting it made
              rows one or two lines tall depending on their content, and the ragged rhythm
              was most of what made this section look unfinished. Follows the Summary step's
              `isSpecified` treatment: the placeholder is muted, never mistakable for a value.
            */}
                {disabled && isRequested && (
                  <Text
                    style={[
                      styles.requestedComment,
                      !storedComment && styles.requestedCommentEmpty,
                    ]}
                  >
                    {storedComment || 'No comment'}
                  </Text>
                )}

                {isOpen && (
                  <View
                    style={[
                      styles.commentPanel,
                      isMobile && styles.commentPanelStacked,
                    ]}
                  >
                    <TextInput
                      ref={node => {
                        commentInputRefs.current.set(type, node);
                      }}
                      style={[
                        styles.commentInput,
                        isMobile && styles.commentInputStacked,
                      ]}
                      value={drafts.get(type) ?? storedComment}
                      onChangeText={value => handleCommentChange(type, value)}
                      onSelectionChange={() => {
                        if (commentCaret?.type === type) {
                          setCommentCaret(null);
                        }
                      }}
                      selection={
                        commentCaret?.type === type
                          ? {
                              start: commentCaret.start,
                              end: commentCaret.end,
                            }
                          : undefined
                      }
                      placeholder="Comment (optional)"
                      placeholderTextColor={colors.inputPlaceholder}
                      multiline
                      textAlignVertical="top"
                      testID={`request-deliverable-comment-${type}`}
                      accessibilityLabel={`Comment for ${label}`}
                    />
                  </View>
                )}
              </View>
            );
          })}

          {/*
            Named, not offered. No checkbox and nothing pressable: a control that looks
            interactive and is not invites clicks that do nothing, and this list exists to
            inform rather than to act. One wrapped line rather than a row each, so the
            catalogue cannot take on the same visual weight as the asks above it — which is
            the one thing it must not do.

            Absent entirely when there is nothing left to name, rather than an empty heading.
          */}
          {availableTypes.length > 0 && (
            <View
              style={styles.availableGroup}
              testID="request-deliverables-available"
            >
              <Text style={styles.availableHeading}>Also available</Text>
              <Text
                style={styles.availableNames}
                testID="request-deliverables-available-names"
              >
                {availableTypes.map(getRequestDeliverableLabel).join(', ')}
              </Text>
            </View>
          )}
        </View>
      </View>
      <FieldTextAssist
        visible={!disabled && assistType != null && assistPanel != null}
        onClose={() => setAssistType(null)}
        title={assistType != null ? getRequestDeliverableLabel(assistType) : ''}
        bullets={assistPanel != null ? [...assistPanel.bullets] : []}
        examplesIntro={assistPanel?.examplesIntro}
        examples={
          assistPanel?.examples != null ? [...assistPanel.examples] : []
        }
        onInsertExample={example => {
          if (assistType == null) {
            return false;
          }
          return insertHelperText(assistType, example.text);
        }}
        {...exampleInsertAvailability(
          assistComment,
          DELIVERABLE_COMMENT_INSERT_MAX,
        )}
        insertActionA11yLabel={
          assistType == null
            ? undefined
            : example =>
                `Insert ${
                  example.label
                } at the end of ${getRequestDeliverableLabel(
                  assistType,
                )} comment`
        }
        testID={
          assistType != null
            ? `request-deliverable-text-assist-panel-${assistType}`
            : undefined
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  /**
   * The `summarySection` card, in tokens. `Summary.tsx:2078-2094` is the pattern every other
   * read-only block on this screen follows, but that file predates the token system and
   * hardcodes its colours, so the values are taken from the tokens that carry them now:
   * `#e9ecef` is `borderCard`, `#f8f9fa` is `surfaceFormBandMuted`, `#f0f0f0` is
   * `borderHairline`. Do not copy the literals back.
   */
  card: {
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.cardBorderRadius,
    marginTop: spacing.fieldRowMarginBottom,
    // Clips the header band's corners to the card radius; without it the band's square
    // corners sit proud of the rounded outline.
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.formCheckboxLabelGap,
    paddingHorizontal: spacing.cardPadding,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    backgroundColor: colors.surfaceFormBandMuted,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
  },
  cardTitle: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: '600',
    color: colors.textPrimary,
    flexShrink: 1,
  },
  /** Neutral pill, matching `FieldFillCountBadge` rather than the Summary step's accent. */
  countBadge: {
    backgroundColor: colors.backgroundSecondary,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    paddingHorizontal: spacing.formCheckboxLabelGap,
    paddingVertical: spacing.charCountMarginTop,
    minWidth: spacing.formCheckboxOuterSize + spacing.formCheckboxLabelGap,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  countText: {
    color: colors.textSecondary,
    fontSize: fontSize.cardSubtitle,
    fontWeight: '600',
    textAlign: 'center',
  },
  cardBody: {
    paddingHorizontal: spacing.cardPadding,
  },
  emptyText: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formControl,
    color: colors.textMuted,
    fontStyle: 'italic',
    paddingVertical: spacing.formCompactActionPaddingVertical,
  },
  row: {
    paddingVertical: spacing.formCompactActionPaddingVertical,
  },
  rowDivided: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderHairline,
  },
  /**
   * The base, inline row. Applies at every width; `rowHeaderStacked` below overrides it under
   * 768 (see the fold note at the top of the component).
   *
   * No `justifyContent: 'space-between'`: that pushed it to the far edge of the section, so
   * on a wide screen a two-word label and its control sat several hundred pixels apart with
   * nothing between them, reading as two unrelated things. `flex-start` plus the gap keeps
   * them a pair. `selectRow`'s `flexShrink` is what stops a long label pushing the toggle
   * off the end instead.
   */
  rowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.formFieldStackedActionGap,
  },
  /**
   * Compact only. `flex-start` rather than the default `stretch` so the toggle stays its own
   * width: stretched, its press target would span the whole card body and a tap anywhere on
   * that line would open a comment box.
   *
   * `gap: 0` because `selectRow` already carries `formCompactActionPaddingVertical` beneath
   * the label; keeping the row's 8pt gap on top of it spaced the two lines like separate rows.
   */
  rowHeaderStacked: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
  },
  /**
   * Checkbox + name + sparkle stay one row at every width. The comment toggle still folds
   * under the label on compact (GitLab #1450); putting the glyph in that trailing group
   * parked it under the name on unticked rows and beside "Hide comment" on ticked ones.
   */
  labelWithAssist: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    alignSelf: 'stretch',
  },
  headerTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.formFieldStackedActionGap,
    flexShrink: 0,
  },
  headerTrailingStacked: {
    alignSelf: 'flex-end',
  },
  textAssistTrigger: {
    flexShrink: 0,
    marginLeft: spacing.infoIconPadding,
  },
  requestedLabel: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    fontWeight: '600',
    color: colors.textPrimary,
  },
  /**
   * Separated from the asks by the same hairline the rows use, so it reads as a different
   * kind of thing rather than as more of the same list.
   */
  availableGroup: {
    borderTopWidth: 1,
    borderTopColor: colors.borderHairline,
    paddingTop: spacing.formCompactActionPaddingVertical,
    paddingBottom: spacing.formCompactActionPaddingVertical,
  },
  availableHeading: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    fontWeight: '600',
    color: colors.textMuted,
    marginBottom: spacing.charCountMarginTop,
  },
  availableNames: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
  },
  /** Read-only only — while editable a stored comment lives behind "Show comment". */
  requestedComment: {
    fontSize: fontSize.cardSubtitle,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
    marginTop: spacing.charCountMarginTop,
  },
  /** Muted and italic, so "No comment" can never be mistaken for someone's actual words. */
  requestedCommentEmpty: {
    color: colors.textMuted,
    fontStyle: 'italic',
  },
  rowAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.infoIconPadding,
    paddingVertical: spacing.infoIconPadding,
    paddingHorizontal: spacing.formFieldStackedActionGap,
  },
  /**
   * Compact only. Flush to the card body's right edge (human decision, 2026-08-17).
   *
   * It was first indented 30pt to line up with the label's text. That looked adrift: 30pt
   * matches the label but nothing else in the card, and it left the whole right half of the
   * line empty, so the link read as floating rather than as belonging to the row above it.
   * Right-aligned, its edge lands on the same line as the count badge and the row dividers,
   * and the empty space falls BETWEEN label and link where it reads as separation.
   *
   * `paddingRight: 0` so the text itself is flush; `rowAction`'s horizontal padding would
   * otherwise inset it 8pt and break the alignment it exists to create. The padding stays on
   * the left, where it only grows the press target.
   */
  rowActionStacked: {
    alignSelf: 'flex-end',
    paddingRight: 0,
    /**
     * The target is bought on the HORIZONTAL axis here, because that is the one that is free.
     *
     * This was `minHeight: minimumTapTarget` (44). It is not any more: 44 centres a 16pt line,
     * so 28pt of the box was empty, and stacked between the label above and the comment panel
     * below that surplus read as a gap nobody chose (human report, 2026-08-17).
     *
     * Vertical cannot be recovered by expansion. `hitSlop` is a no-op on react-native-web
     * 0.21.2 (present only in the shipped `.js.flow` stubs, absent from `dist`), and narrow web
     * takes this branch too. Padding with a cancelling negative margin WOULD work on both, but
     * this control is sandwiched — `selectRow`'s tappable padding is directly above and the
     * comment `TextInput` directly below — and this element paints over both, so any vertical
     * expansion steals taps from a neighbour rather than finding empty space.
     *
     * Horizontal is genuinely empty: the link is `flex-end` on a line holding nothing else, so
     * `paddingLeft` grows the press area leftwards into dead space at zero visual cost. The
     * result is ~24 x ~134 rather than 44 x ~90 — a LARGER area than the HIG box, though the
     * 24pt height meets only the WCAG AA floor, not the 44pt comfort target. That trade is the
     * whole point of `minimumTapTargetCompact`; read its doc before copying this.
     */
    minHeight: spacing.minimumTapTargetCompact,
    paddingLeft: spacing.minimumTapTarget,
    justifyContent: 'center',
  },
  rowActionText: {
    fontSize: fontSize.cardSubtitle,
    /**
     * Explicit, unlike most link text, because this one's line box is load-bearing: it plus
     * `rowAction`'s 2x4 padding IS the control's height, and that height is measured against
     * the WCAG AA 24pt floor. Left undefined, the line box is whatever the platform's default
     * leading produces (~15pt on web, ~16 on native), which put the control at 23 on one
     * platform and 24 on another — passing or failing AA depending on where it rendered.
     * 16 + 8 = 24 exactly, on both.
     */
    lineHeight: lineHeight.formLabel,
    color: colors.textActionLink,
    // Regular, not semibold: the row's own label carries the emphasis, and a bolded blue
    // toggle beside it competed with the thing it acts on.
    fontWeight: fontWeight.regular,
  },
  /**
   * `flexShrink: 1` is load-bearing **above 768**, where the header is still a row: it governs
   * WIDTH there, and is what lets a long label give ground to the toggle rather than pushing it
   * off the end.
   *
   * Below 768 the header is a column, so this shrinks the cross axis and does nothing useful.
   * The guard that matters at those widths is `selectLabel`'s own `flexShrink` — this one was
   * never enough on its own, which is what #1450 turned out to be.
   */
  selectRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.formCheckboxLabelGap,
    paddingVertical: spacing.formCompactActionPaddingVertical,
    flexShrink: 1,
  },
  checkbox: {
    width: spacing.formCheckboxOuterSize,
    height: spacing.formCheckboxOuterSize,
    borderRadius: spacing.formCheckboxOuterRadius,
    borderWidth: spacing.formCheckboxUncheckedBorderWidth,
    borderColor: colors.inputBorderNeutral,
    backgroundColor: colors.backgroundForm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxOn: {
    backgroundColor: colors.formCheckboxSelected,
    borderColor: colors.formCheckboxSelected,
  },
  /**
   * `flexShrink: 1` here is what actually stops the overlap, and it is needed at EVERY width,
   * fold or no fold (GitLab #1450).
   *
   * `selectRow` shrinking is not enough on its own: the `Text` inside it had no `flexShrink`,
   * so once the shrunken parent was narrower than the label, the label rendered straight past
   * it instead of reflowing inside it, and RN does not clip by default. That is what put
   * "Budgetary Quote" underneath "Add comment" rather than merely crowding it. Removing this
   * brings the overlap back on any width where a label outgrows its column.
   */
  selectLabel: {
    fontSize: fontSize.formLabel,
    lineHeight: lineHeight.formLabel,
    color: colors.textPrimary,
    flexShrink: 1,
  },
  /**
   * Full width of the card body, with no left indent.
   *
   * It used to clear the checkbox (20 + 10) so the box lined up under the label, which left
   * a dead 30px channel down the left of every open comment and made the input look inset
   * inside an inset. The card already provides the horizontal rhythm, and a comment box has
   * no reason to be narrower than the card it sits in.
   */
  commentPanel: {
    marginTop: spacing.charCountMarginTop,
    marginBottom: spacing.formFieldStackedActionGap,
  },
  /**
   * Compact only. Drops the panel's own margins, because stacked the toggle above it is a
   * 44pt box holding a 16pt line, so it already contributes ~14pt of clear space below the
   * link text; `row`'s `paddingVertical` supplies the same underneath. Keeping the margins on
   * top of both read as a gap nobody chose.
   */
  commentPanelStacked: {
    marginTop: 0,
    marginBottom: 0,
  },
  /**
   * Compact only. Three lines plus the control's own padding, instead of the 144pt shared
   * text-area floor.
   *
   * 144 is sized for a document field with a card to itself. Here the box is one row inside a
   * list of eight, so at 144 a four-word note was 55% of the row's height and the checklist
   * stopped reading as a checklist. Derived from the type tokens rather than fixed, so it
   * still holds three lines if the type scale moves. It is a FLOOR, not a cap — `multiline`
   * grows the box from here, so a long comment is not squeezed, it just does not reserve the
   * space up front. Web keeps 144, where the card is wide enough to carry it.
   */
  commentInputStacked: {
    minHeight:
      lineHeight.formControl * 3 + spacing.formTextAreaPaddingVertical * 2,
  },
  commentInput: {
    minHeight: spacing.formTextAreaMinHeightNative,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.formControlPaddingHorizontal,
    paddingVertical: spacing.formTextAreaPaddingVertical,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
    backgroundColor: colors.backgroundForm,
  },
});
